package models

import i18n.StringKey
import i18n.tr

/**
 * THE definition of what an account is called on screen.
 *
 * Every surface renders an account as two lines: [primary] identifies the account,
 * [secondary] says which bank it is at. Both come from here.
 *
 * This existed **23 times across 10 files** — the count came from the guard below, after a
 * hand survey had found only 5 of those files. Web alone had the transactions sidebar
 * numbering duplicates by name, the dropdown ten lines above it not disambiguating at all,
 * the dashboard chips numbering AND appending a mask, and a dead AccountListComponent
 * numbering by institution+name. Both mobile dashboards, the iOS transaction list and two
 * SERVER files had their own. They disagreed on screen before anyone typed a nickname, the
 * same way three "months remaining" formulas had one goal costing $417/mo on iOS and
 * $411/mo on web.
 *
 * One of the server copies did not merely render a number — `TransactionRepository` WROTE
 * "Chequing (2)" into `accounts.official_name` at link time, so the numbering outlived any
 * change to rendering.
 *
 * Takes loose fields rather than an account type because five different models call it:
 * web's `PlaidAccount`, the dashboard's own row model, the server's `AccountInfo`, and the
 * iOS and Android account models. Same shape as [TransactionEligibility], for the same reason.
 *
 * There is deliberately NO `(1)`/`(2)` numbering: a custom name is unique per user, and an
 * account without one always shows its mask, so nothing here can be ambiguous.
 */
/**
 * Longest custom account name a user may set, after trimming.
 *
 * Lives here rather than in the server route because the server enforces it and every client
 * quotes it in an error message — two copies of a number is how "months remaining" ended up
 * with three different answers on three screens.
 */
const val ACCOUNT_NAME_MAX_LENGTH = 40

object AccountDisplayName {

    /**
     * The prefix every manual / statement-import account id carries.
     *
     * Manual accounts live in `manual_accounts`, a different table with a different
     * shape — no `item_id`, no `mask`, and no `custom_name` column. The id prefix is
     * the only thing that travels with the account onto every client, so it is the
     * one reliable way to tell the two kinds apart from a UI.
     */
    const val MANUAL_ID_PREFIX = "manual:"

    /**
     * True when this account is a manual / statement-import account.
     *
     * Asked before offering a rename: `PATCH /api/accounts/{id}/custom-name` resolves
     * through the `accounts` table, so a manual account can only ever 404 there.
     * Every surface asks here rather than spelling the prefix out a fourth time.
     */
    fun isManual(accountId: String?): Boolean =
        accountId?.startsWith(MANUAL_ID_PREFIX) == true

    /**
     * The line that identifies the account: the user's [customName] when they set one,
     * otherwise the bank's [officialName] (falling back to [name]) followed by the [mask].
     *
     * A blank [customName] counts as absent — a whitespace nickname must not blank the row.
     */
    fun primary(customName: String?, officialName: String?, name: String, mask: String?): String {
        val custom = customName?.trim().orEmpty()
        if (custom.isNotEmpty()) return custom

        val bankName = officialName?.trim()?.takeIf { it.isNotEmpty() } ?: name
        val maskDigits = mask?.trim().orEmpty()
        return if (maskDigits.isEmpty()) bankName else "$bankName ••$maskDigits"
    }

    /**
     * The account's KIND, for the small line under the name: "Chequing", "Savings",
     * "Credit card".
     *
     * Prefers Plaid's [subtype] and falls back to its [type], because a subtype is missing
     * often enough that the row would otherwise be blank. Android already did exactly this
     * inline and iOS did a subtype-only variant that rendered nothing when it was absent —
     * this is the third caller (web), so the rule lives here rather than being written a
     * third time.
     *
     * Plaid's raw values are English identifiers ("checking", "tfsa"); they are translated
     * through [TYPE_KEYS]. Anything unmapped falls back to the capitalised raw value, so a
     * subtype Plaid adds later degrades to readable English rather than to a blank row.
     *
     * The English side is CANADIAN English — Plaid's "checking" is a chequing account here —
     * and the French side uses Canadian sigles: CELI, REER, CELIAPP, REEE, REEI, CPG. Never
     * the English acronym in French text.
     *
     * Reads the current locale, so it must not be called from the server, where the locale is
     * a process-wide global rather than the reader's. The server only ever calls [primary].
     */
    fun typeLabel(subtype: String?, type: String?): String {
        val raw = subtype?.trim()?.takeIf { it.isNotEmpty() } ?: type?.trim().orEmpty()
        if (raw.isEmpty()) return ""
        TYPE_KEYS[raw.lowercase()]?.let { return it.tr() }
        return raw.replaceFirstChar { it.uppercase() }
    }

    /** Plaid's raw `subtype`/`type` values, lowercased, to the string shown for them. */
    private val TYPE_KEYS: Map<String, StringKey> = mapOf(
        "checking" to StringKey.ACCOUNT_TYPE_CHEQUING,
        "savings" to StringKey.ACCOUNT_TYPE_SAVINGS,
        "credit card" to StringKey.ACCOUNT_TYPE_CREDIT_CARD,
        "tfsa" to StringKey.ACCOUNT_TYPE_TFSA,
        "rrsp" to StringKey.ACCOUNT_TYPE_RRSP,
        "fhsa" to StringKey.ACCOUNT_TYPE_FHSA,
        "resp" to StringKey.ACCOUNT_TYPE_RESP,
        "rdsp" to StringKey.ACCOUNT_TYPE_RDSP,
        "rrif" to StringKey.ACCOUNT_TYPE_RRIF,
        "lira" to StringKey.ACCOUNT_TYPE_LIRA,
        "brokerage" to StringKey.ACCOUNT_TYPE_BROKERAGE,
        "mortgage" to StringKey.ACCOUNT_TYPE_MORTGAGE,
        "cd" to StringKey.ACCOUNT_TYPE_GIC,
        "hsa" to StringKey.ACCOUNT_TYPE_HSA,
        "money market" to StringKey.ACCOUNT_TYPE_MONEY_MARKET,
        "cash management" to StringKey.ACCOUNT_TYPE_CASH_MANAGEMENT,
        "401k" to StringKey.ACCOUNT_TYPE_401K,
        "ira" to StringKey.ACCOUNT_TYPE_IRA,
        "line of credit" to StringKey.ACCOUNT_TYPE_LINE_OF_CREDIT,
        "auto" to StringKey.ACCOUNT_TYPE_AUTO_LOAN,
        "student" to StringKey.ACCOUNT_TYPE_STUDENT_LOAN,
        "depository" to StringKey.ACCOUNT_TYPE_DEPOSITORY,
        "credit" to StringKey.ACCOUNT_TYPE_CREDIT,
        "loan" to StringKey.ACCOUNT_TYPE_LOAN,
        "investment" to StringKey.ACCOUNT_TYPE_INVESTMENT,
        "other" to StringKey.ACCOUNT_TYPE_OTHER,
    )

    /** Which bank this account is at, or null when we do not know. */
    fun secondary(institutionName: String?): String? =
        institutionName?.trim()?.takeIf { it.isNotEmpty() }
}
